/**
 * The "Report a Bug" button and its modal, built out of plain DOM.
 *
 * Deliberately no React and no game imports: this has to keep working when the
 * game bundle fails to load, throws on startup, or has torn its own UI down.
 * Everything lives in a fixed-position layer above the game with its own styles.
 */

import { submitBugReport } from './submitReport';
import { captureScreenshot } from './screenshot';
import { formatReportId } from './reportId';

const Prefix = 'aibr';
/** Above the game's own stacking contexts (its highest is the loading curtain at 12). */
const ZIndex = 2147483000;

const PrivacyUrl = 'https://privacy.autoimmunegame.com';

/**
 * Where the button sits, measured from the top-left of the game's letterboxed UI
 * region so it lands just right of the settings gear.
 *
 * The gear is a 2em FontAwesome icon at a 1em margin, so it occupies roughly
 * 16-48px on both axes; this clears it with a small gap.
 */
const ButtonOffset = { left: 74, top: 24 };

const Styles = `
.${Prefix}-root, .${Prefix}-root * {
	box-sizing: border-box;
	font-family: 'NotoSans', 'Segoe UI', Helvetica, Arial, sans-serif;
}

.${Prefix}-button {
	position: fixed;
	/* left/top are set from script, anchored to the game's UI region */
	top: ${ButtonOffset.top}px;
	left: ${ButtonOffset.left}px;
	z-index: ${ZIndex};
	height: 32px;
	border-radius: 16px;
	border: 2px solid #4a443a;
	background: #e3d9cb;
	color: #4a443a;
	display: flex;
	align-items: center;
	gap: 6px;
	cursor: pointer;
	opacity: 0.75;
	transition: opacity 0.15s ease;
	box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45);
	padding: 0 12px 0 9px;
	font-size: 13px;
	font-weight: bold;
	white-space: nowrap;
	line-height: 1;
}
.${Prefix}-button:hover {
	opacity: 1;
	background: #ffffff;
}
/* Draws attention to the button, eg from the tutorial. See setReportButtonFlashing */
@keyframes ${Prefix}-flash {
	0%, 100% { transform: scale(1); opacity: 0.75; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.45); }
	50% { transform: scale(1.2); opacity: 1; box-shadow: 0 0 14px 6px rgba(255, 215, 0, 0.9); }
}
.${Prefix}-button.${Prefix}-flashing {
	animation: ${Prefix}-flash 0.9s ease-in-out infinite;
}
.${Prefix}-button svg {
	width: 17px;
	height: 17px;
	display: block;
	flex: none;
	pointer-events: none;
}

.${Prefix}-backdrop {
	position: fixed;
	inset: 0;
	z-index: ${ZIndex};
	background: rgba(0, 0, 0, 0.6);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
}

.${Prefix}-modal {
	background: #e3d9cb;
	color: #4a443a;
	border: 4px solid #000;
	border-radius: 12px;
	width: 560px;
	max-width: 100%;
	max-height: 100%;
	overflow-y: auto;
	padding: 20px 24px;
	box-shadow: 0 8px 30px rgba(0, 0, 0, 0.6);
	font-size: 15px;
	line-height: 1.45;
}

.${Prefix}-title {
	margin: 0 0 4px 0;
	font-size: 22px;
	font-weight: bold;
}

.${Prefix}-subtitle {
	margin: 0 0 14px 0;
	font-size: 13px;
	opacity: 0.8;
}

.${Prefix}-label {
	display: block;
	font-weight: bold;
	margin-bottom: 6px;
	font-size: 14px;
}

.${Prefix}-textarea {
	width: 100%;
	min-height: 110px;
	resize: vertical;
	padding: 8px;
	border: 2px solid #4a443a;
	border-radius: 6px;
	background: #f6ecdc;
	color: #031522;
	font-size: 14px;
}
.${Prefix}-textarea:focus {
	outline: 2px solid #031522;
}

.${Prefix}-check {
	display: flex;
	align-items: flex-start;
	gap: 8px;
	margin-top: 12px;
	cursor: pointer;
}
.${Prefix}-check input {
	margin: 2px 0 0 0;
	width: 16px;
	height: 16px;
	flex: none;
	cursor: pointer;
}

.${Prefix}-notice {
	margin-top: 12px;
	padding: 10px 12px;
	border-left: 4px solid #8a6d3b;
	background: rgba(138, 109, 59, 0.12);
	font-size: 12.5px;
}
.${Prefix}-notice a {
	color: #2b4a66;
}

.${Prefix}-actions {
	display: flex;
	justify-content: flex-end;
	align-items: center;
	gap: 10px;
	margin-top: 18px;
}

.${Prefix}-btn {
	border: 2px solid #4a443a;
	border-radius: 6px;
	padding: 8px 16px;
	font-size: 15px;
	cursor: pointer;
	background: #daf3f5;
	color: #4a443a;
}
.${Prefix}-btn:hover:not(:disabled) {
	background: #ffffff;
}
.${Prefix}-btn:disabled {
	opacity: 0.55;
	cursor: default;
}
.${Prefix}-btn-secondary {
	background: transparent;
}

.${Prefix}-status {
	margin-top: 12px;
	font-size: 13.5px;
	min-height: 1.2em;
}
.${Prefix}-status-error { color: #8c2f2f; }
.${Prefix}-status-success { color: #2f6b34; }

.${Prefix}-id-panel {
	margin-top: 14px;
	padding: 12px 14px;
	border: 2px dashed #4a443a;
	border-radius: 8px;
	background: #f6ecdc;
	text-align: center;
}
.${Prefix}-id-label {
	font-size: 12.5px;
	margin-bottom: 4px;
}
.${Prefix}-id-value {
	font-family: 'Courier New', Courier, monospace;
	font-size: 22px;
	font-weight: bold;
	letter-spacing: 0.12em;
	color: #031522;
	/* The whole point is that they can select and copy it */
	user-select: all;
	cursor: text;
}
.${Prefix}-id-hint {
	font-size: 12px;
	margin-top: 6px;
	opacity: 0.8;
}
.${Prefix}-copy {
	margin-top: 8px;
	font-size: 12.5px;
	padding: 4px 10px;
}
`;

/** A bug, drawn from primitives so the modal needs no icon library. */
const BugIconSvg = `
<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"
	fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">
	<ellipse cx="12" cy="13.5" rx="5" ry="6.5" fill="currentColor" stroke="none"/>
	<path d="M9 6.5a3 3 0 0 1 6 0"/>
	<path d="M9.2 4.6 7.6 3M14.8 4.6 16.4 3"/>
	<path d="M7 10.5 3.2 8.6M7 14H2.8M7.4 17.6 3.8 20M17 10.5l3.8-1.9M17 14h4.2M16.6 17.6 20.2 20"/>
</svg>
`;

type ModalState = 'idle' | 'sending' | 'sent';

/**
 * Keeps the button beside the settings gear.
 *
 * The gear lives inside the game's letterboxed 16:9 UI region, which is centred
 * in the window - so on a wider-than-16:9 display its left edge is inset and a
 * plain viewport offset would leave the two far apart. This measures that region
 * when it exists and falls back to the viewport when it doesn't, which is the
 * case that matters when the game failed to start.
 */
function anchorToGameUi(button: HTMLElement) {
	const place = () => {
		const region = document.getElementById('user-interface');
		const box = region?.getBoundingClientRect();
		// An unmounted or collapsed region tells us nothing; stay at the viewport.
		const origin = box && box.width > 0 ? box : { left: 0, top: 0 };
		button.style.left = `${Math.round(origin.left) + ButtonOffset.left}px`;
		button.style.top = `${Math.round(origin.top) + ButtonOffset.top}px`;
	};

	place();
	window.addEventListener('resize', place);

	// Following the region's size is a refinement; noticing it appear at all is
	// what actually places the button. Kept independent so a browser missing one
	// observer doesn't cost us the other.
	const sizeWatcher = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
	const follow = (region: Element) => {
		sizeWatcher?.observe(region);
		place();
	};

	// The game's UI region is mounted by React well after this bundle runs.
	const existing = document.getElementById('user-interface');
	if (existing) {
		follow(existing);
	} else if (typeof MutationObserver !== 'undefined') {
		const waitForRegion = new MutationObserver(() => {
			const region = document.getElementById('user-interface');
			if (!region) return;
			waitForRegion.disconnect();
			follow(region);
		});
		waitForRegion.observe(document.body, { childList: true, subtree: true });
	}
}

/** Where the report button is on screen, eg to point it out. Null if it isn't mounted */
export function getReportButtonRect(): DOMRect | null {
	return document.getElementById(`${Prefix}-button`)?.getBoundingClientRect() ?? null;
}

/** Long enough for anyone to notice it, in case whoever turned it on never turns it off */
const FlashTimeoutMs = 20000;
let flashTimeout: ReturnType<typeof setTimeout> | null = null;

/** Pulses the report button to draw attention to it, until turned off or it times out */
export function setReportButtonFlashing(flash: boolean) {
	if (flashTimeout !== null) {
		clearTimeout(flashTimeout);
		flashTimeout = null;
	}
	document.getElementById(`${Prefix}-button`)?.classList.toggle(`${Prefix}-flashing`, flash);
	if (flash) {
		flashTimeout = setTimeout(() => setReportButtonFlashing(false), FlashTimeoutMs);
	}
}

export function createBugReportUi() {
	if (document.getElementById(`${Prefix}-button`)) return; // already mounted

	const style = document.createElement('style');
	style.id = `${Prefix}-styles`;
	style.textContent = Styles;
	document.head.appendChild(style);

	const button = document.createElement('button');
	button.id = `${Prefix}-button`;
	button.className = `${Prefix}-root ${Prefix}-button`;
	button.type = 'button';
	button.title = 'Report a bug';
	button.setAttribute('aria-label', 'Report a bug');
	button.innerHTML = `${BugIconSvg}<span>Report Bug</span>`;
	document.body.appendChild(button);
	anchorToGameUi(button);

	let backdrop: HTMLDivElement | null = null;
	let state: ModalState = 'idle';

	// Captured on button press, before the modal exists, so the player's own
	// screen is what gets photographed. Held until they submit or close.
	let pendingScreenshot: Uint8Array | null = null;
	let capturing = false;

	// The game reads mouse and key events off the document, so nothing that
	// happens inside our UI is allowed to reach it - otherwise typing a report
	// fires hotkeys and clicking drops cells on the board.
	const swallow = (element: HTMLElement) => {
		const events = ['mousedown', 'mouseup', 'click', 'dblclick', 'mousemove', 'wheel',
			'keydown', 'keyup', 'keypress', 'pointerdown', 'pointerup', 'touchstart', 'touchend'];
		for (const name of events) {
			element.addEventListener(name, e => e.stopPropagation());
		}
	};
	swallow(button);

	const closeModal = () => {
		backdrop?.remove();
		backdrop = null;
		state = 'idle';
		// Don't keep a few MB of PNG around for a report that was never sent.
		pendingScreenshot = null;
	};

	const openModal = () => {
		if (backdrop) return;

		backdrop = document.createElement('div');
		backdrop.className = `${Prefix}-root ${Prefix}-backdrop`;
		backdrop.innerHTML = `
			<div class="${Prefix}-modal" role="dialog" aria-modal="true" aria-labelledby="${Prefix}-title">
				<h2 class="${Prefix}-title" id="${Prefix}-title">Report a Bug</h2>
				<p class="${Prefix}-subtitle">Send us what happened so we can fix it.</p>

				<label class="${Prefix}-label" for="${Prefix}-description">What went wrong? (optional)</label>
				<textarea class="${Prefix}-textarea" id="${Prefix}-description" maxlength="5000"
					placeholder="What were you doing when the problem happened?"></textarea>

				${pendingScreenshot ? `
				<label class="${Prefix}-check">
					<input type="checkbox" id="${Prefix}-screenshot" checked>
					<span>Capture Screenshot</span>
				</label>` : ''}
				<label class="${Prefix}-check">
					<input type="checkbox" id="${Prefix}-sysinfo" checked>
					<span>Share System Information</span>
				</label>

				<p class="${Prefix}-subtitle">
					Every report includes the game's logs and your most recent autosaves, so we can
					reload the game where it went wrong. Nothing else stored on your device is read.
				</p>

				<div class="${Prefix}-notice">
					System information includes things like your graphics card, driver version, CPU
					and available memory. Details like these could be used to identify your machine.
					They are sent only to the Auto Immune developers, are used only to reproduce and
					fix this bug, and are never made public or shared with anyone else.
					<a href="${PrivacyUrl}" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
				</div>

				<div class="${Prefix}-status" id="${Prefix}-status"></div>

				<div class="${Prefix}-id-panel" id="${Prefix}-id-panel" hidden>
					<div class="${Prefix}-id-label">Your report ID</div>
					<div class="${Prefix}-id-value" id="${Prefix}-id-value"></div>
					<button type="button" class="${Prefix}-btn ${Prefix}-copy" id="${Prefix}-copy">Copy ID</button>
					<div class="${Prefix}-id-hint">
						Quote this if you email us or post about the problem, so we can find your report.
					</div>
				</div>

				<div class="${Prefix}-actions">
					<button type="button" class="${Prefix}-btn ${Prefix}-btn-secondary" id="${Prefix}-cancel">Cancel</button>
					<button type="button" class="${Prefix}-btn" id="${Prefix}-submit">Submit Report</button>
				</div>
			</div>
		`;
		document.body.appendChild(backdrop);
		swallow(backdrop);

		const modal = backdrop.querySelector(`.${Prefix}-modal`) as HTMLElement;
		const description = backdrop.querySelector(`#${Prefix}-description`) as HTMLTextAreaElement;
		// Absent on builds that can't screenshot - the option isn't offered there.
		const screenshotCheck = backdrop.querySelector(`#${Prefix}-screenshot`) as HTMLInputElement | null;
		const sysInfoCheck = backdrop.querySelector(`#${Prefix}-sysinfo`) as HTMLInputElement;
		const status = backdrop.querySelector(`#${Prefix}-status`) as HTMLElement;
		const submit = backdrop.querySelector(`#${Prefix}-submit`) as HTMLButtonElement;
		const cancel = backdrop.querySelector(`#${Prefix}-cancel`) as HTMLButtonElement;
		const idPanel = backdrop.querySelector(`#${Prefix}-id-panel`) as HTMLElement;
		const idValue = backdrop.querySelector(`#${Prefix}-id-value`) as HTMLElement;
		const copyButton = backdrop.querySelector(`#${Prefix}-copy`) as HTMLButtonElement;

		const setStatus = (text: string, kind?: 'error' | 'success') => {
			status.textContent = text;
			status.className = `${Prefix}-status` + (kind ? ` ${Prefix}-status-${kind}` : '');
		};

		const showReportId = (reportId: string) => {
			idValue.textContent = formatReportId(reportId);
			idPanel.hidden = false;
			copyButton.onclick = async () => {
				try {
					// The raw id, not the dashed display form, is what we can look up.
					await navigator.clipboard.writeText(reportId);
					copyButton.textContent = 'Copied';
				} catch {
					// Clipboard access can be refused; the id is selectable anyway.
					copyButton.textContent = 'Press Ctrl+C to copy';
				}
			};
		};

		// Clicking the dimmed area closes, clicking the panel itself must not.
		modal.addEventListener('click', e => e.stopPropagation());
		// Once sent, only the explicit Close button dismisses this - a stray click
		// on the dimmed area must not throw away the report id they need to keep.
		backdrop.addEventListener('click', () => { if (state === 'idle') closeModal(); });
		backdrop.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Escape' && state !== 'sending') closeModal();
		});

		cancel.addEventListener('click', () => { if (state !== 'sending') closeModal(); });

		submit.addEventListener('click', async () => {
			if (state === 'sending') return;
			if (state === 'sent') { closeModal(); return; }

			state = 'sending';
			submit.disabled = true;
			cancel.disabled = true;
			description.disabled = true;
			if (screenshotCheck) screenshotCheck.disabled = true;
			sysInfoCheck.disabled = true;
			setStatus('Collecting logs and sending report…');

			// The checkbox only decides whether the image is sent. The image itself
			// is held until the report goes through or the modal closes, so a
			// failed attempt can be retried with the box either way - a second
			// capture at this point would only photograph this dialog.
			const sendScreenshot = screenshotCheck?.checked ?? false;

			const result = await submitBugReport({
				description: description.value,
				includeSystemInfo: sysInfoCheck.checked,
				screenshot: sendScreenshot ? pendingScreenshot : null,
			});

			if (result.ok) {
				state = 'sent';
				// Done with it either way - sent, or deliberately left out.
				pendingScreenshot = null;
				setStatus('Thanks! Your report was sent.', 'success');
				showReportId(result.reportId);
				submit.textContent = 'Close';
				submit.disabled = false;
				// Deliberately no auto-close here: the id is the one thing the
				// player has to take away with them, so the modal waits for them.
			} else {
				state = 'idle';
				setStatus(result.message, 'error');
				submit.disabled = false;
				cancel.disabled = false;
				description.disabled = false;
				sysInfoCheck.disabled = false;
				// Still holding the image, so the choice is still a real one.
				if (screenshotCheck) screenshotCheck.disabled = false;
			}
		});

		description.focus();
	};

	button.addEventListener('click', async () => {
		if (backdrop) { closeModal(); return; }
		// A second press while the shot is being taken would start another one.
		if (capturing) return;

		capturing = true;
		try {
			// Straight away, with no waiting: this is the screen the player is
			// reporting on, and the only thing of ours in it is this button.
			pendingScreenshot = await captureScreenshot();
		} finally {
			capturing = false;
		}
		openModal();
	});
}
